import classes from 'classnames';
import React, { ReactNode, useEffect, useRef } from 'react';
import Card from 'components/Card/Card';
import CloseIcon from 'assets/icons/close-3.svg?react';
import styles from './Modal.module.scss';

export type Props = {
  onClose?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | MouseEvent,
  ) => void;
  children?: ReactNode;
  backdropClose?: boolean;
  className?: string;
  classNameOverlay?: string;
  containerClassName?: string;
  dataTest?: string;
  withCloseButton?: boolean;
  contentClassName?: string;
};

function getScrollbarWidth() {
  const hasVerticalScrollbar =
    document.body.scrollHeight > document.documentElement.clientHeight;

  if (!hasVerticalScrollbar) {
    return 0;
  }

  const outer = document.createElement('div');

  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  document.body.appendChild(outer);

  const inner = document.createElement('div');

  outer.appendChild(inner);

  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  outer.parentNode?.removeChild(outer);

  return scrollbarWidth;
}

function Modal({
  onClose,
  children,
  backdropClose = true,
  className,
  classNameOverlay,
  containerClassName,
  dataTest = 'modal',
  withCloseButton = true,
  contentClassName,
}: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (overlayRef.current && backdropClose) {
      overlayRef.current.addEventListener('mouseup', (event) => {
        if (onClose) {
          onClose(event);
        }
      });
    }
  }, [overlayRef, onClose, backdropClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${getScrollbarWidth()}px`;

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0';
    };
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.addEventListener('mouseup', (event) => {
        event.stopPropagation();
      });
    }
  }, [containerRef]);

  return (
    <div
      ref={overlayRef}
      className={classes(styles.overlay, classNameOverlay)}
      data-test={dataTest}
    >
      <div
        className={classes(styles.container, styles.fade, containerClassName)}
      >
        <Card
          className={classes(styles.card, className)}
          ref={containerRef}
          dataTest={`${dataTest}-card`}
        >
          {onClose && withCloseButton && (
            <button
              type="button"
              className={styles['close-button']}
              onClick={onClose}
              data-test={`${dataTest}-btn`}
            >
              <CloseIcon
                className={styles.close}
                data-test={`${dataTest}-close-btn`}
              />
            </button>
          )}
          <div className={contentClassName} data-test={`${dataTest}-content`}>
            {children}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Modal;
